/**
 * Cookie Utility Service
 *
 * Centralized cookie management for authentication data.
 * Provides secure storage for access tokens, refresh tokens, and user credentials.
 */

/**
 * Set a cookie with optional expiration and security settings
 * @param {string} name - Cookie name
 * @param {string|object} value - Cookie value (will be JSON stringified if object)
 * @param {number|Date|null} expires - Number of days until expiration, Date object, or null for session cookie (default: 7)
 * @param {object} options - Additional cookie options
 */
export function setCookie(name, value, expires = 7, options = {}) {
  try {
    let cookieString = "";

    // Convert objects to JSON string
    const cookieValue = typeof value === "object"
      ? JSON.stringify(value)
      : String(value);

    // Encode the value to handle special characters
    cookieString = `${encodeURIComponent(name)}=${encodeURIComponent(cookieValue)}`;

    // Set expiration - handle different types of expiration values
    if (expires !== null && expires !== undefined) {
      let expirationDate;

      if (typeof expires === 'number') {
        // Handle days (backward compatibility)
        expirationDate = new Date();
        expirationDate.setTime(expirationDate.getTime() + (expires * 24 * 60 * 60 * 1000));
      } else if (expires instanceof Date) {
        // Handle Date object (from backend)
        expirationDate = expires;
      } else {
        // Default to 7 days if invalid type
        expirationDate = new Date();
        expirationDate.setTime(expirationDate.getTime() + (7 * 24 * 60 * 60 * 1000));
      }

      cookieString += `; expires=${expirationDate.toUTCString()}`;
    }
    // If expires is null/undefined, it becomes a session cookie (no expiration)

    // Set path (default to root)
    cookieString += `; path=${options.path || "/"}`;

    // Set domain if provided
    if (options.domain) {
      cookieString += `; domain=${options.domain}`;
    }

    // Set secure flag (only over HTTPS) - recommended for production
    if (options.secure || window.location.protocol === "https:") {
      cookieString += "; secure";
    }

    // Set SameSite attribute for CSRF protection
    cookieString += `; SameSite=${options.sameSite || "Lax"}`;

    document.cookie = cookieString;

    return true;
  } catch (error) {
    console.error("Error setting cookie:", error);
    return false;
  }
}

/**
 * Get a cookie value by name
 * @param {string} name - Cookie name
 * @param {boolean} parseJSON - Whether to parse the value as JSON (default: false)
 * @returns {string|object|null} - Cookie value or null if not found
 */
export function getCookie(name, parseJSON = false) {
  try {
    const nameEQ = encodeURIComponent(name) + "=";
    const cookies = document.cookie.split(";");

    for (let i = 0; i < cookies.length; i++) {
      let cookie = cookies[i];
      while (cookie.charAt(0) === " ") {
        cookie = cookie.substring(1, cookie.length);
      }
      if (cookie.indexOf(nameEQ) === 0) {
        const value = decodeURIComponent(
          cookie.substring(nameEQ.length, cookie.length)
        );

        if (parseJSON) {
          try {
            return JSON.parse(value);
          } catch (e) {
            console.warn("Failed to parse cookie as JSON:", name);
            return value;
          }
        }

        return value;
      }
    }

    return null;
  } catch (error) {
    console.error("Error getting cookie:", error);
    return null;
  }
}

/**
 * Remove a cookie by name
 * @param {string} name - Cookie name
 * @param {object} options - Cookie options (path, domain)
 */
export function removeCookie(name, options = {}) {
  try {
    // Set expiration to past date to remove cookie
    setCookie(name, "", -1, options);
    return true;
  } catch (error) {
    console.error("Error removing cookie:", error);
    return false;
  }
}

/**
 * Check if a cookie exists
 * @param {string} name - Cookie name
 * @returns {boolean}
 */
export function hasCookie(name) {
  return getCookie(name) !== null;
}

/**
 * Clear all cookies (use with caution)
 * @param {object} options - Cookie options (path, domain)
 */
export function clearAllCookies(options = {}) {
  try {
    const cookies = document.cookie.split(";");

    for (let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i];
      const eqPos = cookie.indexOf("=");
      const name = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim();
      removeCookie(name, options);
    }

    return true;
  } catch (error) {
    console.error("Error clearing cookies:", error);
    return false;
  }
}

// ============================================
// Authentication-specific Cookie Functions
// ============================================

const AUTH_COOKIE_NAMES = {
  CREDENTIALS: "MP_CREDENTIALS",
  SELECTED_BRANCH: "MP_SELECTED_BRANCH",
};

// Fields excluded from MP_CREDENTIALS cookie.
// These come from /merchants/session/context API on page refresh.
// accessToken & refreshToken are handled via HttpOnly cookies by the backend.
const SESSION_CONTEXT_FIELDS = [
  "accessToken",
  "refreshToken",
  "id",
  "merchantId",
  "locationId",
  "businessName",
  "topicToSubscribe",
  "userId",
];

/**
 * Store authentication credentials in cookies
 * @param {object} credentials - User credentials object
 * @param {object} expirationTimes - Expiration times for different tokens (optional)
 * @param {Date} expirationTimes.accessToken - Access token expiration date
 * @param {Date} expirationTimes.refreshToken - Refresh token expiration date
 * @param {Date} expirationTimes.credentials - Credentials expiration date
 */
export function setAuthCredentials(credentials, expirationTimes = {}) {
  if (!credentials) {
    console.error("Cannot set empty credentials");
    return false;
  }

  try {
    // Determine if we're in production (HTTPS)
    const isProduction = window.location.protocol === "https:";

    // Use provided expiration times or default to 7 days for credentials
    const credentialsExp = expirationTimes.credentials || 7;

    // Strip session context fields (tokens + server-side data) before storing in cookie
    const cookieCredentials = { ...credentials };
    SESSION_CONTEXT_FIELDS.forEach((field) => {
      delete cookieCredentials[field];
    });

    // Store credentials without sensitive/session fields
    setCookie(AUTH_COOKIE_NAMES.CREDENTIALS, cookieCredentials, credentialsExp, {
      secure: isProduction,
      sameSite: "Lax",
    });

    return true;
  } catch (error) {
    console.error("Error setting auth credentials:", error);
    return false;
  }
}

/**
 * Get authentication credentials from cookies
 * @returns {object|null} - Credentials object or null
 */
export function getAuthCredentials() {
  return getCookie(AUTH_COOKIE_NAMES.CREDENTIALS, true);
}

/**
 * Get access token — no longer stored in cookies.
 * Tokens are managed via HttpOnly cookies by the backend.
 * @returns {null}
 * @deprecated Tokens are now in HttpOnly cookies; use session context API instead.
 */
export function getAccessToken() {
  return null;
}

/**
 * Get refresh token — no longer stored in cookies.
 * @returns {null}
 * @deprecated Tokens are now in HttpOnly cookies; use session context API instead.
 */
export function getRefreshToken() {
  return null;
}

/**
 * Update access token — no-op.
 * Tokens are managed via HttpOnly cookies by the backend.
 * @deprecated Tokens are now in HttpOnly cookies.
 */
export function updateAccessToken() {
  return true;
}

/**
 * Remove all authentication cookies
 */
export function clearAuthCookies() {
  removeCookie(AUTH_COOKIE_NAMES.CREDENTIALS);
  removeCookie(AUTH_COOKIE_NAMES.SELECTED_BRANCH);
}

/**
 * Check if user is authenticated (has valid credentials)
 * @returns {boolean}
 */
export function isAuthenticated() {
  const credentials = getAuthCredentials();
  return credentials && credentials.role ? true : false;
}

/**
 * Set selected branch data
 * @param {object} branchData - Branch data object
 * @param {number|Date} [expiration] - Expiration time for the branch data (optional)
 */
export function setSelectedBranch(branchData, expiration) {
  if (!branchData) return false;

  const isProduction = window.location.protocol === "https:";

  // Use provided expiration or default to 30 days
  const branchExp = expiration !== undefined ? expiration : 30;

  return setCookie(AUTH_COOKIE_NAMES.SELECTED_BRANCH, branchData, branchExp, {
    secure: isProduction,
    sameSite: "Lax",
  });
}

/**
 * Get selected branch data
 * @returns {object|null}
 */
export function getSelectedBranch() {
  return getCookie(AUTH_COOKIE_NAMES.SELECTED_BRANCH, true);
}

/**
 * Remove selected branch data
 */
export function clearSelectedBranch() {
  removeCookie(AUTH_COOKIE_NAMES.SELECTED_BRANCH);
}

// ============================================
// Migration Helper Functions
// ============================================

/**
 * Migrate data from localStorage to cookies
 * This function helps in transitioning from localStorage to cookies
 */
export function migrateFromLocalStorage() {
  try {
    // Migrate credentials
    const credentialsFromLS = localStorage.getItem("CREDENTIALS");
    if (credentialsFromLS) {
      try {
        const credentials = JSON.parse(credentialsFromLS);
        setAuthCredentials(credentials);
      } catch (e) {
        console.error("Failed to parse credentials from localStorage:", e);
      }
    }

    // Migrate selected branch
    const branchFromLS = localStorage.getItem("SELECTED_BRANCH_DATA");
    if (branchFromLS && branchFromLS !== "undefined") {
      try {
        const branch = JSON.parse(branchFromLS);
        setSelectedBranch(branch);
      } catch (e) {
        console.error("Failed to parse branch data from localStorage:", e);
      }
    }

    return true;
  } catch (error) {
    console.error("Error during migration from localStorage:", error);
    return false;
  }
}

/**
 * Check if migration is needed
 * @returns {boolean}
 */
export function needsMigration() {
  // If we have data in localStorage but not in cookies, migration is needed
  const hasLocalStorageData = localStorage.getItem("CREDENTIALS") !== null;
  const hasCookieData = hasCookie(AUTH_COOKIE_NAMES.CREDENTIALS);

  return hasLocalStorageData && !hasCookieData;
}

// ============================================
// Redux Persist Cookie Storage Adapter
// ============================================

const COOKIE_PREFIX = 'persist:';

/**
 * Cookie storage adapter for redux-persist
 * Provides getItem, setItem, removeItem interface
 */
export const cookieStorage = {
  /**
   * Get an item from cookie storage
   * @param {string} key - Storage key
   * @returns {Promise<string|null>}
   */
  getItem: async (key) => {
    try {
      const cookieName = `${COOKIE_PREFIX}${key}`;
      const value = getCookie(cookieName, false);
      return value;
    } catch (error) {
      console.error('Error getting item from cookie storage:', error);
      return null;
    }
  },

  /**
   * Set an item in cookie storage
   * @param {string} key - Storage key
   * @param {string} value - Value to store (already JSON stringified by redux-persist)
   * @param {number|Date} [expiration] - Expiration time for the cookie (optional, defaults to 30 days)
   * @returns {Promise<void>}
   */
  setItem: async (key, value, expiration) => {
    try {
      const cookieName = `${COOKIE_PREFIX}${key}`;
      const isProduction = window.location.protocol === 'https:';

      // Use provided expiration or default to 30 days
      const cookieExp = expiration !== undefined ? expiration : 30;

      setCookie(cookieName, value, cookieExp, {
        secure: isProduction,
        sameSite: 'Lax',
      });
    } catch (error) {
      console.error('Error setting item in cookie storage:', error);
    }
  },

  /**
   * Remove an item from cookie storage
   * @param {string} key - Storage key
   * @returns {Promise<void>}
   */
  removeItem: async (key) => {
    try {
      const cookieName = `${COOKIE_PREFIX}${key}`;
      removeCookie(cookieName);
    } catch (error) {
      console.error('Error removing item from cookie storage:', error);
    }
  },
};
